import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { FeedSourceListItem } from '@news-cms/shared';
import { ApiError, api } from './api';

export interface MgrInboxItem {
  id: string;
  topic: string;
  intro: string;
  publishedAt: string | null;
  imageUrl: string;
  thumbUrl: string;
  status: 'new' | 'imported';
  importedPostId: string | null;
}

export interface MgrImportResult {
  postId: string;
  postSlug: string;
  mediaId: string;
  alreadyImported: boolean;
}

export interface MgrRefreshResult {
  ok: true;
  inserted: number;
  cleaned: number;
}

export const mgrInboxQueryOptions = queryOptions({
  queryKey: ['feed', 'mgronline', 'list'] as const,
  queryFn: () => api<{ items: MgrInboxItem[] }>('/api/admin/feed/mgronline').then((d) => d.items),
  // Worker caches upstream in KV for 10 min — match that on the client so
  // we don't show stale "new" status after re-import in another tab.
  staleTime: 60_000,
});

/** GET /source returns the source row + the separately-stored c.php URL
 * (which lives in the settings table, not feed_sources). The dialog needs
 * both, so we keep them grouped in a wrapper rather than splitting into
 * two queries. */
export interface MgrSourceInfo {
  source: FeedSourceListItem;
  detailUrl: string;
}

/** Current source row + detail URL — drives the settings dialog prefill
 * and the "last polled" hint. */
export const mgrSourceQueryOptions = queryOptions({
  queryKey: ['feed', 'mgronline', 'source'] as const,
  queryFn: () => api<MgrSourceInfo>('/api/admin/feed/mgronline/source'),
  staleTime: 60_000,
});

export function useRefreshMgrFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<MgrRefreshResult>('/api/admin/feed/mgronline/refresh', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mgrInboxQueryOptions.queryKey });
      void qc.invalidateQueries({ queryKey: mgrSourceQueryOptions.queryKey });
    },
  });
}

export interface UpdateMgrSourceInput {
  url?: string;
  name?: string;
  isActive?: boolean;
  fetchIntervalMinutes?: number;
  description?: string | null;
  /** c.php endpoint — stored in `settings` table, not feed_sources. */
  detailUrl?: string;
}

export function useUpdateMgrSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMgrSourceInput) =>
      api<MgrSourceInfo>('/api/admin/feed/mgronline/source', {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mgrInboxQueryOptions.queryKey });
      void qc.invalidateQueries({ queryKey: mgrSourceQueryOptions.queryKey });
    },
  });
}

export function useImportMgrItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ result: MgrImportResult }>(`/api/admin/feed/mgronline/${id}/import`, {
        method: 'POST',
      }).then((d) => d.result),
    onSuccess: () => qc.invalidateQueries({ queryKey: mgrInboxQueryOptions.queryKey }),
  });
}

export function mgrErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  switch (e.code) {
    case 'UPSTREAM_ERROR':
      return 'ดึงข้อมูลจาก Manager Online ไม่สำเร็จ — ลองอีกครั้งหรือกด "ดึงใหม่"';
    case 'ITEM_NOT_FOUND':
      return 'รายการนี้ไม่อยู่ใน feed แล้ว — refresh แล้วลองใหม่';
    case 'VALIDATION_ERROR':
      return 'ข้อมูลไม่ถูกต้อง — ตรวจ URL / ค่าอื่นแล้วลองใหม่';
    case 'FORBIDDEN':
      return 'ไม่มีสิทธิ์ — ต้องมี feed.manage';
    case 'NOT_FOUND':
      return 'ไม่พบแหล่งข้อมูล mgronline — รีเฟรชหน้าแล้วลองใหม่';
    default:
      return e.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  }
}
